/* eslint-disable prettier/prettier */
'use client';

import * as React from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';

import { cn } from 'utils/shadcn';
import { Button } from 'app/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from 'app/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'app/components/ui/popover';
import { useBlogsSlice } from '../../slice';
import { useFormContext } from 'react-hook-form';

interface Category {
  id: number;
  name: string;
  parent_id: number | null;
  subcategories?: Category[];
}

export default function Categories() {
  const form = useFormContext();
  const { useGetAllCategoriesQuery } = useBlogsSlice();
  const [getCategories, { data: categories, isLoading }] =
    useGetAllCategoriesQuery();

  React.useEffect(() => {
    getCategories({});
  }, []);

  const [open, setOpen] = React.useState(false);
  const [selectedCategories, setSelectedCategories] = React.useState<
    Category[]
  >([]);

  // Handle category selection
  const handleCategorySelect = (category: Category) => {
    setSelectedCategories(prev => {
      // Check if category is already selected
      const isSelected = prev.some(cat => cat.id === category.id);

      if (isSelected) {
        // Remove category and its subcategories if selected
        return prev.filter(
          cat => cat.id !== category.id && cat.parent_id !== category.id,
        );
      } else {
        // Add category if not selected
        return [...prev, category];
      }
    });
  };

  // Handle subcategory selection
  const handleSubCategorySelect = (parentId: number, subCategory: Category) => {
    setSelectedCategories(prev => {
      // Check if subcategory is already selected
      const isSelected = prev.some(cat => cat.id === subCategory.id);

      if (isSelected) {
        // Remove only the subcategory
        return prev.filter(cat => cat.id !== subCategory.id);
      } else {
        // Add subcategory
        return [...prev, subCategory];
      }
    });
  };

  // Remove selected category/subcategory
  const handleRemoveCategory = (categoryId: number) => {
    setSelectedCategories(prev => prev.filter(cat => cat.id !== categoryId));
  };

  // Update form value when selections change
  React.useEffect(() => {
    form.setValue(
      'categories',
      selectedCategories.map(cat => cat.id),
    );
  }, [selectedCategories, form]);

  return (
    <div className="rounded-md bg-white  dark:bg-transparent p-4">
      <h2 className="text-sm mb-2 font-semibold">Categories</h2>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full h-auto py-2 justify-start font-normal relative"
          >
            <div className="flex flex-wrap gap-2 justify-start">
              {selectedCategories.length > 0
                ? selectedCategories.map(category => (
                    <div
                      key={category.id}
                      className="px-2 py-1 rounded-xl border bg-slate-200 text-xs font-medium flex items-center gap-1"
                    >
                      {category.name}
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          handleRemoveCategory(category.id);
                        }}
                        className="hover:bg-slate-300 rounded-full p-0.5"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))
                : 'Select categories'}
            </div>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50 absolute right-1" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0">
          <Command>
            <CommandInput placeholder="Search categories" />
            <CommandEmpty>No categories found.</CommandEmpty>
            <CommandGroup>
              <CommandList>
                {categories?.response?.map((category: Category) => (
                  <div key={category.id}>
                    {/* Main Category */}
                    <CommandItem
                      value={category.name}
                      onSelect={() => handleCategorySelect(category)}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          selectedCategories.some(cat => cat.id === category.id)
                            ? 'opacity-100'
                            : 'opacity-0',
                        )}
                      />
                      {category.name}
                    </CommandItem>

                    {/* Subcategories */}
                    {category.subcategories?.map(subCategory => (
                      <CommandItem
                        key={subCategory.id}
                        value={subCategory.name}
                        onSelect={() =>
                          handleSubCategorySelect(category.id, subCategory)
                        }
                        className="pl-8 text-gray-500 text-sm"
                      >
                        <Check
                          className={cn(
                            'mr-2 h-4 w-4',
                            selectedCategories.some(
                              cat => cat.id === subCategory.id,
                            )
                              ? 'opacity-100'
                              : 'opacity-0',
                          )}
                        />
                        {subCategory.name}
                      </CommandItem>
                    ))}
                  </div>
                ))}
              </CommandList>
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
